import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import SEOHead from '../components/SEOHead';
import { BlogPostingSchema, BreadcrumbSchema } from '../components/JsonLdSchema';
import { getPostBySlug, posts } from '../blog/index';

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

const mdComponents = {
  h2: ({ children }) => (
    <h2 style={{ fontSize: 'clamp(20px, 2.5vw, 26px)', fontWeight: 800, color: '#111827', marginTop: 48, marginBottom: 16, lineHeight: 1.3 }}>
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginTop: 32, marginBottom: 12 }}>
      {children}
    </h3>
  ),
  p: ({ children }) => (
    <p style={{ fontSize: 16, color: '#374151', lineHeight: 1.8, marginBottom: 20 }}>
      {children}
    </p>
  ),
  ul: ({ children }) => (
    <ul style={{ paddingLeft: 24, marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol style={{ paddingLeft: 24, marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li style={{ fontSize: 16, color: '#374151', lineHeight: 1.7 }}>
      {children}
    </li>
  ),
  strong: ({ children }) => (
    <strong style={{ color: '#111827', fontWeight: 700 }}>{children}</strong>
  ),
  blockquote: ({ children }) => (
    <blockquote style={{
      borderLeft: '4px solid #CC0000', paddingLeft: 20, margin: '28px 0',
      background: '#fef2f2', borderRadius: '0 8px 8px 0', padding: '16px 20px',
    }}>
      {children}
    </blockquote>
  ),
  a: ({ href, children }) => {
    const style = { color: '#CC0000', fontWeight: 600, textDecoration: 'underline' };
    const isExternal = /^https?:\/\//.test(href || '');
    if (isExternal) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" style={style}>
          {children}
        </a>
      );
    }
    return (
      <Link to={href} style={style}>
        {children}
      </Link>
    );
  },
  table: ({ children }) => (
    <div style={{ overflowX: 'auto', marginBottom: 24 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        {children}
      </table>
    </div>
  ),
  th: ({ children }) => (
    <th style={{
      background: '#111827', color: '#fff', padding: '10px 14px',
      textAlign: 'left', fontWeight: 700, fontSize: 13,
    }}>
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td style={{
      padding: '10px 14px', borderBottom: '1px solid #e5e7eb', color: '#374151',
    }}>
      {children}
    </td>
  ),
  tr: ({ children }) => (
    <tr style={{ transition: 'background 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      {children}
    </tr>
  ),
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) return <Navigate to="/404" replace />;

  const otherPosts = posts.filter(p => p.slug !== slug).slice(0, 3);

  return (
    <main style={{ paddingTop: 64 }}>
      <SEOHead
        title={`${post.title} | DPOS Blog`}
        description={post.description}
        keywords={post.keywords}
        canonical={`/blog/${post.slug}`}
      />
      <BlogPostingSchema post={post} />
      <BreadcrumbSchema items={[
        { name: 'Home', path: '/' },
        { name: 'Blog', path: '/blog' },
        { name: post.title },
      ]} />

      {/* Article header */}
      <section style={{
        padding: '56px 24px 48px',
        background: 'linear-gradient(135deg, #fff 0%, #fef2f2 100%)',
        borderBottom: '1px solid #fecaca',
      }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <Link to="/blog" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 28,
            transition: 'color 0.2s',
          }}
            onMouseEnter={e => e.currentTarget.style.color = '#CC0000'}
            onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
          >
            ← Back to Blog
          </Link>

          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 600 }}>
              {formatDate(post.date)}
            </span>
            <span style={{
              fontSize: 11, fontWeight: 700, background: '#fef2f2',
              color: '#CC0000', padding: '2px 10px', borderRadius: 100,
            }}>
              {post.readTime}
            </span>
          </div>

          <h1 style={{
            fontSize: 'clamp(26px, 3.5vw, 40px)',
            fontWeight: 800, color: '#111827', lineHeight: 1.25, marginBottom: 20,
          }}>
            {post.title}
          </h1>

          <p style={{ fontSize: 17, color: '#6b7280', lineHeight: 1.75, marginBottom: 20 }}>
            {post.description}
          </p>

          <p style={{ fontSize: 13, color: '#9ca3af', fontWeight: 600 }}>
            Written by Mubashir Sayed, Founder of DPOS
          </p>
        </div>
      </section>

      {/* Article body */}
      <section style={{ padding: '48px 24px 80px', background: '#fff' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <ReactMarkdown components={mdComponents}>
            {post.content}
          </ReactMarkdown>

          {/* Author bio */}
          <div style={{
            marginTop: 48, padding: '20px 24px',
            background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12,
          }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
              Mubashir Sayed
            </p>
            <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>
              Founder of DPOS, building restaurant POS and billing software for the Indian F&amp;B industry.
            </p>
          </div>

          {/* CTA inside post */}
          <div style={{
            marginTop: 56,
            background: 'linear-gradient(135deg, #CC0000 0%, #A80000 100%)',
            borderRadius: 16, padding: '36px 32px', textAlign: 'center',
          }}>
            <h3 style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 12 }}>
              Ready to Try the Best POS Software?
            </h3>
            <p style={{ fontSize: 15, color: '#fecaca', marginBottom: 24, lineHeight: 1.6 }}>
              Join 100+ restaurants on DPOS. Book a free demo today — no commitment required.
            </p>
            <Link to="/contact" style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: '#fff', color: '#CC0000',
              padding: '12px 28px', borderRadius: 8, fontSize: 15, fontWeight: 700,
            }}>
              Contact for Free Demo →
            </Link>
          </div>
        </div>
      </section>

      {/* Related posts */}
      {otherPosts.length > 0 && (
        <section style={{ padding: '0 24px 80px', background: '#f9fafb' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <h2 style={{ fontSize: 24, fontWeight: 800, color: '#111827', marginBottom: 28, paddingTop: 48 }}>
              More Articles
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 20,
            }}>
              {otherPosts.map(p => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    padding: '24px 20px',
                    display: 'block',
                    transition: 'box-shadow 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                >
                  <p style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {formatDate(p.date)}
                  </p>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', lineHeight: 1.4, marginBottom: 8 }}>
                    {p.title}
                  </h3>
                  <span style={{ fontSize: 13, color: '#CC0000', fontWeight: 600 }}>Read →</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
